import type { S2PElevateSlide } from "@/data/s2pElevateAI";

// Use the same fictional source snapshot for both builds so the review stays comparable.
const followUpBuildBrief = `Build a small interactive follow-up review for a meeting owner.

Use only these fictional sources:
N1: Customer handoff. Owner: Maya. Due: September 30.
T1: Customer handoff. Owner: Maya. Due: October 1.
N2: Confirm next customer review. Owner and due date are missing.

Show the action, owner, due date, issue, and source reference. Keep both conflicting dates visible. Mark missing values UNKNOWN and ask the reviewer to resolve them. Add a filter for items needing review. Never invent an owner or date.

Use no connected apps. Do not send messages or change source records. Keep the result private for review.`;

export const ELEVATE_BUILD_OUTPUT_SLIDES: S2PElevateSlide[] = [
  {
    id: "session-4-claude-artifacts",
    session: 4,
    kind: "prompt",
    tone: "paper",
    eyebrow: "Session 4 · Build and review · 2 minutes",
    title: "Make the output usable with Claude Artifacts",
    lead: "Ask Claude to turn the follow-up draft into an interactive review tool. Check the source rows and behavior before sharing it.",
    promptLabel: "Try this in Claude",
    prompt: `How might we turn a follow-up draft into an interactive Claude Artifact, within private review and fictional-data boundaries, based on the sources below?\n\n${followUpBuildBrief}`,
    note: "Artifacts can hold documents, dashboards, and interactive tools. Confirm feature availability in your approved workspace. If access is unavailable, review the sample rows on paper.",
    durationMinutes: 2,
    sourceIds: ["part4", "ex-4-1", "claude.artifacts"],
    sourceUrl: "https://support.claude.com/en/articles/17153992-what-are-artifacts-and-how-do-i-use-them",
    sourceLabel: "Claude Artifacts documentation",
  },
  {
    id: "session-4-chatgpt-sites",
    session: 4,
    kind: "prompt",
    tone: "navy",
    eyebrow: "Session 4 · Build and review · 2 minutes",
    title: "Build the same review tool with ChatGPT Sites",
    lead: "Use the same inputs and review standard. Sites can host a website or app that you can reopen, refine, and share with a selected audience.",
    promptLabel: "Try this with Sites in ChatGPT",
    prompt: `How might we build a follow-up review website with ChatGPT Sites, within private review and fictional-data boundaries, based on the sources below?\n\n${followUpBuildBrief}\n\nSave a version without deploying it. Stop for my review before deployment or any change to the audience.`,
    warning: "Sites is in public beta. Check your plan and workspace settings. A Sites deployment is live; saving a version lets you review it before deployment.",
    durationMinutes: 2,
    sourceIds: ["part4", "ex-4-1", "chatgpt.sites"],
    sourceUrl: "https://learn.chatgpt.com/docs/sites",
    sourceLabel: "ChatGPT Sites documentation",
  },
  {
    id: "session-4-build-output-review",
    session: 4,
    kind: "concept",
    tone: "cloud",
    eyebrow: "Session 4 · Human review · 2 minutes",
    title: "Test the tool before you share it",
    lead: "Use the same checks whether the output is a written draft, an Artifact, or a Site.",
    sections: [
      { label: "Evidence", title: "Check every row", body: "Can you trace it to N1, T1, or N2? Keep both conflicting dates and flag the missing owner and date." },
      { label: "Behavior", title: "Try the filter", body: "Both actions need review. Does the filter show both? Does it preserve the original values and issue flags?" },
      { label: "Audience", title: "Review who can use it", body: "Check sharing, connected data, and what is saved between visits. The owner approves deployment or broader access." },
    ],
    note: "An interface lets a person use the output. A recurring agent still needs a trigger, approved sources, run controls, and an accountable owner. Save one correction for Session 5.",
    durationMinutes: 2,
    sourceIds: ["part4", "ex-4-1", "claude.artifacts", "chatgpt.sites"],
  },
];

export const ELEVATE_TOKEN_BUDGET_PROMPT = "My budget is X. Find a way to track your token spend, and ensure you complete the task before the budget runs out.";

export const ELEVATE_TOKEN_BUDGET_SLIDE: S2PElevateSlide = {
  id: "session-5-token-budget-prompt",
  session: 5,
  kind: "prompt",
  tone: "paper",
  eyebrow: "Session 5 · Improve the run · 2 minutes",
  title: "Give the agent a budget for the run",
  lead: "Treat budget as a constraint alongside quality. Keep the same task and review checks when comparing runs.",
  promptLabel: "Budget prompt · Replace X before you run",
  prompt: ELEVATE_TOKEN_BUDGET_PROMPT,
  sections: [
    { label: "Define", title: "Give X a unit", body: "State a token allowance or a dollar limit. If it is dollars, confirm the model rates and which charges are included." },
    { label: "Measure", title: "Use reported usage", body: "Ask for the available usage or budget tracker. Record spent and remaining budget; say when either is unavailable." },
    { label: "Finish", title: "Leave room for checks", body: "Reserve capacity for verification and the final handoff. If the task will not fit, report the gap before spending more." },
  ],
  warning: "Use a tool or runtime control for a hard limit. The prompt alone cannot enforce a cap or guarantee completion. Never invent token counts or skip the review to fit the budget.",
  durationMinutes: 2,
  sourceIds: ["part5", "elevate.user-budget-prompt", "openai.run-budget-controls"],
  sourceUrl: "https://developers.openai.com/cookbook/articles/per_run_spending_controller_responses_api",
  sourceLabel: "OpenAI guide to controlling spend per run",
};

export const ELEVATE_PRODUCTIVITY_PROMPT = "based on the average employee time to do all of the work across all of my chat sessions the last week, how much time did I realistically save in time and wages";

export const ELEVATE_PRODUCTIVITY_SLIDE: S2PElevateSlide = {
  id: "session-5-productivity-gains-prompt",
  session: 5,
  kind: "prompt",
  tone: "cloud",
  eyebrow: "Session 5 · Weekly productivity review",
  title: "Did AI save you time?",
  lead: "Use a weekly estimate to choose what to measure. Compare completed tasks at the same quality standard.",
  promptLabel: "Weekly productivity review · Example prompt",
  prompt: ELEVATE_PRODUCTIVITY_PROMPT,
  sections: [
    { label: "Evidence", title: "Check what it can see", body: "Use visible chats or an approved export for a stated week. List missing sessions. Count each completed task once." },
    { label: "Time", title: "Compare the full effort", body: "Record each task's manual baseline, AI use, review, and rework. Show net hours as a range with sources, assumptions, and confidence." },
    { label: "Value", title: "Put a value on the time", body: "Multiply net hours by an agreed hourly rate. Show tool costs separately. This values the time freed; it only counts as payroll savings if payroll spending fell." },
  ],
  warning: "Chat history alone cannot prove time saved. Ask for missing timings or wage rates, and label estimates.",
  durationMinutes: 2,
  sourceIds: ["part5", "elevate.user-productivity-prompt"],
};

export const ELEVATE_CONTINUOUS_IMPROVEMENT_PROMPT = "Look at the past 100 sessions, which skills weren’t used or if we have duplicate skills. Interview me if helpful so we can clean up and look at the conversations to see how might we improve the skills. Then, Look at the most called skills, read the sessions for those skills, and tell me the feedback from the conversations that we could use to improve the skills";

export const ELEVATE_CONTINUOUS_IMPROVEMENT_SLIDE: S2PElevateSlide = {
  id: "session-5-continuous-improvement-prompt",
  session: 5,
  kind: "prompt",
  tone: "paper",
  eyebrow: "Session 5 · Continuous improvement",
  title: "Review the skills your agents use",
  lead: "Use session history to find what needs work. Turn repeated feedback into a proposed skill change.",
  promptLabel: "Skill review · Example prompt",
  prompt: ELEVATE_CONTINUOUS_IMPROVEMENT_PROMPT,
  sections: [
    { label: "Inspect", title: "Check the sample", body: "Inspect the last 100 sessions you can access and the current skill inventory. Count recorded calls. State any missing sessions or logs." },
    { label: "Compare", title: "Check for overlap", body: "Compare unused or overlapping skills. Low usage alone doesn't make a skill unnecessary. Check the instructions and conversation evidence before recommending a cleanup." },
    { label: "Improve", title: "Propose one change", body: "For frequently used skills, cite the sessions and feedback behind each proposed edit. Ask about intent when helpful. Test one approved change on the same task." },
  ],
  warning: "Missing logs are a coverage gap, not proof a skill was unused. Get approval before editing, combining, or deleting skills.",
  note: "Repeat the review at an agreed cadence. Keep the previous version so you can compare results.",
  durationMinutes: 2,
  sourceIds: ["part5", "elevate.user-continuous-improvement-prompt"],
};
